import { useTranslation } from 'react-i18next';
import type { IChecklistItem } from '@interfaces/IScheduler';
import type { ChecklistEvidenceKind } from '@d-orca/shared/checklist';
import { CameraIcon, MessageSquareIcon, TagIcon } from '@utils/icons';
import { EVIDENCE_KINDS } from '@d-orca/shared/checklist';
import styles from './ChecklistTags.module.css';

const ICON = { photo: CameraIcon, comment: MessageSquareIcon, blackboard: TagIcon } as const;

/**
 * What a check asks for, at a glance: one tag per evidence kind it accepts,
 * amber when the kind is required (the tick is refused without it), grey when
 * it is optional. Nothing for a description, which asks for nothing.
 */
export function ChecklistEvidenceTags({ item, className = '' }: { item: IChecklistItem; className?: string }) {
  const kinds = EVIDENCE_KINDS.filter((kind) => item.kind === 'check' && item[kind] !== 'none');
  if (kinds.length === 0) return null;
  return (
    <span className={`${styles.tags} ${className}`}>
      {kinds.map((kind) => (
        <EvidenceTag key={kind} kind={kind} required={item[kind] === 'required'} />
      ))}
    </span>
  );
}

interface IEvidenceTagProps {
  kind: ChecklistEvidenceKind;
  required: boolean;
  /** Beside a heading that already names the kind, the tag says only whether
   *  the tick waits on it. */
  modeOnly?: boolean;
}

/** One tag: the kind's icon and short name, and whether it is required. */
export function EvidenceTag({ kind, required, modeOnly = false }: IEvidenceTagProps) {
  const { t } = useTranslation();
  const Icon = ICON[kind];
  const mode = t(`scheduler.panel.checklist.mode.${required ? 'required' : 'optional'}`);
  if (modeOnly) return <span className={required ? styles.required : styles.tag}>{mode}</span>;
  return (
    <span className={required ? styles.required : styles.tag} title={mode} aria-label={`${t(`scheduler.panel.checklist.tags.${kind}`)} · ${mode}`}>
      <Icon size={11} />
      {t(`scheduler.panel.checklist.tags.${kind}`)}
    </span>
  );
}
